import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import useDialogs from '@/hooks/useDialogs';
import useWebhooks from '@/hooks/useWebhooks';
import { cn } from '@/lib/utils';
import { selectSelectedMessageNodesStack } from '@/modules/tryIt/redux/selectors';
import { TYPES, iconByType } from '@/redux/dialogs/helper';
import { getActionsSelector } from '@/redux/dialogs/selectors';
import { popModal } from '@/redux/modals/actions';
import { requestActionSelection, requestNodeSelection } from '@/redux/nodes/actions';
import { selectCondition } from '@/redux/nodes/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { DebugStep, DebugStepSubType, DebugStepType } from '@/shared/types/tryIt';
import { capitalizeFirstLetter } from '@/util/util';

import { ContextChanges } from './ContextChanges';

import styles from '../DetailsSection.module.scss';

/**
 * Extracts and flattens items from dialogs based on a specified key
 *
 * This function iterates through all dialogs and their nodes to extract items
 * (like conditions or requisites) and enriches them with contextual information
 * including node and dialog metadata.
 *
 * @param dialogs - Array of dialog objects containing nodes
 * @param key - The property key to extract from each node (e.g., 'conditions', 'requisites')
 * @returns Array of enriched items with additional context (node_id, node_name, dialog_id, dialog_name, index)
 */
const getDialogsKey = (dialogs, key: string) => {
  let allConditions = [];
  if (!dialogs) {
    return allConditions;
  }

  dialogs.forEach((item) => {
    item.nodes.forEach((node) => {
      // Concatenate the conditions of this node to the allConditions array
      if (Array.isArray(node[key])) {
        const conditions = node[key].map((condition, index) => ({
          ...condition,
          node_id: node.node_id,
          node_name: node.name,
          dialog_id: item.dialog_id,
          dialog_name: item.name,
          index,
        }));
        allConditions = allConditions.concat(conditions);
      }
    });
  });

  return allConditions;
};

interface BrainStepProps {
  step: DebugStep;
}

export const BrainStep = ({ step }: BrainStepProps) => {
  // ===== HOOKS =====
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const selectedMessageNodesStack = useSelector(selectSelectedMessageNodesStack);

  // ===== DATA FETCHING =====
  const { dialogs: mainDialogs } = useDialogs(brainId);
  const allConditions = getDialogsKey(mainDialogs, 'conditions');
  const allRequisites = getDialogsKey(mainDialogs, 'requisites');
  const webhookId = step?.webhook_id;
  const brainIdForWebhook = webhookId ? brainId : null;
  const { dialogs: webhookDialogs } = useDialogs(brainIdForWebhook);
  const allActions = getActionsSelector(webhookDialogs);
  const { webhook } = useWebhooks(brainIdForWebhook, webhookId);

  // ===== COMPUTED VALUES =====
  const dialogsForAccessibility =
    step.action_type === 'event' || step.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED
      ? mainDialogs
      : webhookDialogs;

  const accessibleNodeIds = useMemo(
    () =>
      dialogsForAccessibility
        ? new Set(
            dialogsForAccessibility.flatMap((d) =>
              Array.isArray(d.nodes) ? d.nodes.map((n) => n.node_id) : []
            )
          )
        : new Set(),
    [dialogsForAccessibility]
  );

  const isTargetNodeAccessible = accessibleNodeIds.has(step.node_id);

  const nodeToDialogMap = useMemo(
    () =>
      dialogsForAccessibility
        ? new Map(
            dialogsForAccessibility.flatMap((d) =>
              Array.isArray(d.nodes) ? d.nodes.map((n) => [n.node_id, d.dialog_id]) : []
            )
          )
        : new Map(),
    [dialogsForAccessibility]
  );

  const jumpToNode = selectedMessageNodesStack?.find((node) => node.node_id === step?.node_id);

  const condition = allConditions.find(
    (condition) => condition.condition_id === step?.condition_id
  );

  const requisite = allRequisites.find(
    (requisite) => requisite.requisite_id === step?.requisite_id
  );

  const isRequisite = !!step.requisite_id;

  const stepType =
    step.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED
      ? 'workflow_triggered'
      : step.type === DebugStepType.WEBHOOK
        ? 'webhook'
        : step.type === DebugStepType.MCP_TOOL
          ? 'mcp_tool'
          : step.type === DebugStepType.AI_RESPONSE
            ? 'ai_response'
            : step.action_type || (isRequisite ? 'question' : 'condition');

  // ===== EVENT HANDLERS =====
  const handleGoToAction = useCallback(() => {
    if (!brainId || !step.action_id) {
      return;
    }

    const selectedAction = allActions.find(
      (a: { action_id: string }) => a.action_id === step.action_id
    );

    if (!selectedAction) {
      return;
    }

    dispatch(popModal());
    dispatch(
      requestActionSelection({ node_id: selectedAction.node_id, action_id: step.action_id })
    );

    navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${selectedAction.dialog_id}`);
  }, [allActions, brainId, dispatch, navigate, slug, step.action_id]);

  const handleGoToAIResponseAction = useCallback(() => {
    if (!brainId || !step.action_id || !step.node_id) {
      return;
    }
    const dialog = mainDialogs?.find((d) =>
      Array.isArray(d.nodes) ? d.nodes.some((n) => n.node_id === step.node_id) : false
    );
    if (!dialog?.dialog_id) {
      return;
    }
    dispatch(popModal());
    dispatch(requestActionSelection({ node_id: step.node_id, action_id: step.action_id }));
    navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialog.dialog_id}`);
  }, [mainDialogs, brainId, dispatch, navigate, slug, step.action_id, step.node_id]);

  const handleGoToNode = useCallback(() => {
    if (!isTargetNodeAccessible) {
      return;
    }

    const dialogId = nodeToDialogMap.get(step.node_id);
    if (!dialogId) {
      return;
    }

    navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialogId}`);
    dispatch(popModal());
    dispatch(requestNodeSelection(step.node_id));
  }, [isTargetNodeAccessible, nodeToDialogMap, brainId, dispatch, navigate, slug, step.node_id]);

  const handleGoToCondition = useCallback(
    (condition) => () => {
      navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${condition.dialog_id}`);
      dispatch(popModal());
      dispatch(selectCondition({ nodeId: condition.node_id, index: condition.index }));
    },
    [brainId, dispatch, navigate, slug]
  );

  // ===== HELPER FUNCTIONS =====
  const getTagMessage = () => {
    const tagCount = step.tags?.length || 0;
    const joinedTags = step.tags?.join(', ');

    switch (step.tag_operation) {
      case 'remove':
        return t('try_it.details.tags_removed', { count: tagCount, 0: joinedTags });
      case 'clear':
        return t('try_it.details.tags_cleared');
      case 'add':
      default:
        return t('try_it.details.tags_applied', { count: tagCount, 0: joinedTags });
    }
  };

  // ===== RENDER =====
  const icon = iconByType({
    id: stepType,
    size: 16,
    type: TYPES.ACTION,
    color: 'var(--muted-foreground)',
    stepsMode: true,
  });

  const renderStepBody = () => {
    switch (stepType) {
      case 'workflow_triggered':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.node_triggered', { 0: jumpToNode?.name || step.node_id })}{' '}
            {isTargetNodeAccessible && (
              <button
                type="button"
                className={cn(
                  'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal',
                  styles.link
                )}
                onClick={handleGoToNode}
              >
                {t('try_it.details.go_to_node')}
              </button>
            )}
          </span>
        );

      case 'ai_response':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.ai_response_generated')}{' '}
            <button
              type="button"
              className={cn(
                'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal',
                styles.link
              )}
              onClick={handleGoToAIResponseAction}
            >
              {t('actions.types.ai_response')}
            </button>
          </span>
        );

      case 'text':
        return (
          <div className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.text_message_sent')}
          </div>
        );

      case 'image':
      case 'carousel':
      case 'webview':
      case 'video':
      case 'file':
      case 'url':
      case 'survey':
        return (
          <div className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.action_type_sent', { 0: capitalizeFirstLetter(step.action_type) })}
          </div>
        );

      case 'whatsapp_flow':
        return (
          <div className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.whatsapp_flow_sent')}
          </div>
        );

      case 'question_set':
        return (
          <div className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.action_type_sent', { 0: t('actions.types.question_set') })}
          </div>
        );

      case 'googlesheet':
      case 'email':
      case 'replay':
        return (
          <div className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.action_type_called', { 0: capitalizeFirstLetter(step.action_type) })}
          </div>
        );

      case 'webhook':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('actions.types.webhook')}{' '}
            <button
              type="button"
              className={cn(
                'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal',
                styles.link
              )}
              onClick={handleGoToAction}
            >
              {webhook?.name}
            </button>{' '}
            {t('try_it.details.response_time', { 0: step.duration })}
          </span>
        );

      case 'mcp_tool':
        return (
          <>
            <span className="text-sm leading-5 font-normal text-muted-foreground">
              {t('try_it.details.tool_invocation', { 0: step.mcp_tool_name })}
              {step.duration !== undefined && (
                <> {t('try_it.details.response_time', { 0: step.duration })}</>
              )}
            </span>
            {step.context_changes && Object.keys(step.context_changes).length > 0 && (
              <ContextChanges context_changes={step.context_changes} />
            )}
          </>
        );

      case 'tag':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {getTagMessage()}
          </span>
        );

      case 'pause':
      case 'close':
      case 'handover':
        return (
          <span
            className={cn('text-sm leading-5 font-normal text-muted-foreground', styles.capitalize)}
          >
            {t('try_it.details.conversation', { 0: step.action_type })}
          </span>
        );

      case 'switch_channel':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('actions.types.switch_channel')}
          </span>
        );

      case 'reset':
      case 'set_variables':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('common.context')}
            <ContextChanges context_changes={step.context_changes} />
          </span>
        );

      case 'event':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('actions.types.jump_to')}{' '}
            <button
              type="button"
              className={cn(
                'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal',
                styles.link,
                { [styles.disabled]: !isTargetNodeAccessible }
              )}
              onClick={handleGoToNode}
              disabled={!isTargetNodeAccessible}
            >
              {jumpToNode?.name}
            </button>{' '}
            {t('dialog.jump_to.label')}
          </span>
        );

      case 'condition':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('actions.types.condition')}{' '}
            <button
              type="button"
              className={cn(
                'inline border-0 bg-transparent p-0 text-sm leading-5 font-normal',
                styles.link
              )}
              onClick={handleGoToCondition(condition)}
            >
              {condition?.name}
            </button>{' '}
            {t('try_it.details.was_true')}
          </span>
        );

      case 'question':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('try_it.details.question_to_fill_in')}{' '}
            <span className="bg-muted/50 text-sm leading-5 font-normal text-muted-foreground">
              {requisite?.save_as}
            </span>{' '}
            {t('try_it.details.value_asked')}
          </span>
        );

      case 'reminder':
      case 'on_inactivity':
        return (
          <span className="text-sm leading-5 font-normal text-muted-foreground">
            {t('actions.types.reminder')}
            {step.reminder_seconds && ` (${step.reminder_seconds}s)`}
          </span>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {icon}
      {renderStepBody()}
    </>
  );
};
